import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, RefreshCw, Copy, Check } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import AiDisclaimer from './shared/AiDisclaimer';
import ListenButton from './shared/ListenButton';

const TABS = [
  { key: 'ciso',       label: 'CISO'   },
  { key: 'cfo',        label: 'CFO'    },
  { key: 'cio',        label: 'CIO'    },
  { key: 'ceo',        label: 'CEO'    },
  { key: 'board',      label: 'Board'  },
  { key: 'anomaly',    label: 'Anomaly'},
  { key: 'summary30d', label: '30-Day' },
];

// Static class maps to avoid Tailwind purge stripping dynamic class names
const ACCENT = {
  blue: {
    icon:    'text-blue-500',
    tabActive: 'border-blue-500 text-blue-700',
    btn:     'bg-blue-600 hover:bg-blue-700',
    spinner: 'text-blue-400',
  },
  purple: {
    icon:    'text-purple-500',
    tabActive: 'border-purple-500 text-purple-700',
    btn:     'bg-purple-600 hover:bg-purple-700',
    spinner: 'text-purple-400',
  },
  rose: {
    icon:    'text-rose-500',
    tabActive: 'border-rose-500 text-rose-700',
    btn:     'bg-rose-600 hover:bg-rose-700',
    spinner: 'text-rose-400',
  },
  emerald: {
    icon:    'text-emerald-500',
    tabActive: 'border-emerald-500 text-emerald-700',
    btn:     'bg-emerald-600 hover:bg-emerald-700',
    spinner: 'text-emerald-400',
  },
  amber: {
    icon:    'text-amber-500',
    tabActive: 'border-amber-500 text-amber-700',
    btn:     'bg-amber-600 hover:bg-amber-700',
    spinner: 'text-amber-400',
  },
  indigo: {
    icon:    'text-indigo-500',
    tabActive: 'border-indigo-500 text-indigo-700',
    btn:     'bg-indigo-600 hover:bg-indigo-700',
    spinner: 'text-indigo-400',
  },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/**
 * Generic intelligence narrative component.
 *
 * Props:
 *   type        — narrative type: "main" | "ransomware" | "enterprise" | "darkweb" | "ai" | "bu" | "tprm"
 *   title       — header title text
 *   subtitle    — header subtitle text
 *   accentColor — color key from ACCENT map (default "blue")
 */
export default function IntelligenceNarrative({
  type = 'main',
  title = 'Intelligence Narrative',
  subtitle = 'AI-generated risk briefing',
  accentColor = 'blue',
}) {
  const apiCall   = useApi();
  const accent    = ACCENT[accentColor] ?? ACCENT.blue;

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [activeTab, setActiveTab] = useState('ciso');
  const cacheRef       = useRef({});
  const activeVoiceRef = useRef(null);

  const fetchNarrative = useCallback(async (voice) => {
    if (cacheRef.current[voice]) {
      setData(cacheRef.current[voice]);
      setLoading(false);
      return;
    }
    activeVoiceRef.current = voice;
    setLoading(true);
    setError(null);
    try {
      const result = await apiCall('GET', `/narrative/${type}?voice=${voice}`);
      if (activeVoiceRef.current !== voice) return; // tab switched mid-flight — discard stale response
      cacheRef.current[voice] = result;
      setData(result);
    } catch (e) {
      if (activeVoiceRef.current !== voice) return;
      setError(e?.response?.data?.message ?? 'Narrative generation failed. Please try again.');
    } finally {
      if (activeVoiceRef.current === voice) setLoading(false);
    }
  }, [apiCall, type]);

  // Auto-fetch CISO voice on mount
  useEffect(() => { fetchNarrative('ciso'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = (voice) => {
    setActiveTab(voice);
    fetchNarrative(voice);
  };

  const handleRegenerate = async () => {
    cacheRef.current = {}; // invalidate all cached voices on regenerate
    setData(null);
    setError(null);
    setLoading(true);
    try {
      const result = await apiCall('POST', `/narrative/${type}/regenerate?voice=${activeTab}`);
      cacheRef.current[activeTab] = result;
      setData(result);
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Narrative generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const activeText = data?.narrative ?? '';

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Sparkles className={`h-4 w-4 ${accent.icon} flex-shrink-0`} />
          <div>
            <span className="text-base font-semibold text-gray-900">{title}</span>
            <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* The saved narrative is served until Regenerate, its first view or a day old, so the
              time it was written is shown rather than a "data updated" prompt. */}
          {data?.generatedAtUtc && (
            <span className="text-xs text-gray-400">
              Generated {fmtDate(data.generatedAtUtc)}
            </span>
          )}
          <button
            onClick={handleRegenerate}
            disabled={loading}
            className={`flex items-center gap-1.5 text-sm font-medium text-white ${accent.btn} disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors`}
          >
            {loading
              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5" />}
            {loading ? 'Generating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Initial loading state (no data yet) */}
      {loading && !data && (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          <RefreshCw className={`h-5 w-5 animate-spin mx-auto mb-2 ${accent.spinner}`} />
          Generating briefing…
        </div>
      )}

      {/* Tab bar + content — shown once we have data or are refreshing existing data */}
      {(data || (loading && activeText)) && (
        <>
          <div className="flex border-b border-gray-100 px-5 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? accent.tabActive
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {loading && activeTab === tab.key && (
                  <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" />
                )}
              </button>
            ))}
          </div>

          <div className="px-5 py-4">
            {loading && !activeText ? (
              <div className={`text-center text-sm text-gray-400 py-4`}>
                <RefreshCw className={`h-5 w-5 animate-spin mx-auto mb-2 ${accent.spinner}`} />
                Generating briefing…
              </div>
            ) : activeText ? (
              <div className="flex items-start justify-between gap-4">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line flex-1">
                  {activeText}
                </p>
                <div className="flex-shrink-0 mt-0.5 flex flex-col items-start gap-1">
                  <CopyButton text={activeText} />
                  <ListenButton text={activeText} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                This briefing was unavailable during generation.
              </p>
            )}
          </div>
          <AiDisclaimer />
        </>
      )}
    </div>
  );
}
